/**
 * Widget Configuration Interface
 * Unified configuration system for all widget types with dynamic options
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';
import { WIDGET_TYPES, UniversalWidget } from './UnifiedWidgetSystem';

// Configuration Panel Styles
const ConfigPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;
  background: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  max-height: 600px;
  overflow-y: auto;
`;

const ConfigSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SectionTitle = styled.h3`
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  margin: 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${theme.colors.border.secondary};
`;

const ConfigRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.text.primary};
`;

const Input = styled.input`
  padding: 0.5rem;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.fontSizes.sm};
  background: ${theme.colors.background.default};
  color: ${theme.colors.text.primary};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 2px ${theme.colors.primary.main}20;
  }
`;

const Select = styled.select`
  padding: 0.5rem;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.fontSizes.sm};
  background: ${theme.colors.background.default};
  color: ${theme.colors.text.primary};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 2px ${theme.colors.primary.main}20;
  }
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  margin-right: 0.5rem;
`;

const PreviewContainer = styled.div`
  padding: 1rem;
  background: ${theme.colors.background.default};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border.secondary};
  min-height: 200px;
`;

const ZoneEditor = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  background: ${theme.colors.background.default};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border.secondary};
`;

const ZoneRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  gap: 0.5rem;
  align-items: center;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  background: ${props => props.variant === 'danger' ? theme.colors.error.main : theme.colors.primary.main};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.fontSizes.sm};
  cursor: pointer;
  
  &:hover {
    opacity: 0.9;
  }
`;

/**
 * Widget Type Selector
 */
const WidgetTypeSelector = ({ selectedType, onTypeChange }) => {
  return (
    <ConfigSection>
      <SectionTitle>Widget Type</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.5rem' }}>
        {Object.entries(WIDGET_TYPES).map(([type, config]) => (
          <div
            key={type}
            onClick={() => onTypeChange(type)}
            style={{
              padding: '1rem',
              textAlign: 'center',
              border: `2px solid ${selectedType === type ? theme.colors.primary.main : theme.colors.border.primary}`,
              borderRadius: theme.borderRadius.md,
              cursor: 'pointer',
              background: selectedType === type ? theme.colors.primary.main + '10' : 'transparent'
            }}
          >
            <div style={{ fontSize: theme.fontSizes.xl, marginBottom: '0.5rem' }}>
              {config.icon}
            </div>
            <div style={{ fontSize: theme.fontSizes.sm, fontWeight: theme.fontWeights.medium }}>
              {config.name}
            </div>
            <div style={{ fontSize: theme.fontSizes.xs, color: theme.colors.text.secondary, marginTop: '0.25rem' }}>
              {config.description}
            </div>
          </div>
        ))}
      </div>
    </ConfigSection>
  );
};

/**
 * Data Source Selector
 */
const DataSourceSelector = ({ dataPaths, selectedPaths, onPathChange, multiSelect = false }) => {
  return (
    <ConfigSection>
      <SectionTitle>Data Source</SectionTitle>
      {multiSelect ? (
        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
          {dataPaths.map(path => (
            <div key={path} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
              <Checkbox
                checked={selectedPaths.includes(path)}
                onChange={(e) => {
                  if (e.target.checked) {
                    onPathChange([...selectedPaths, path]);
                  } else {
                    onPathChange(selectedPaths.filter(p => p !== path));
                  }
                }}
              />
              <span style={{ fontSize: theme.fontSizes.sm }}>{path}</span>
            </div>
          ))}
        </div>
      ) : (
        <Select
          value={selectedPaths[0] || ''}
          onChange={(e) => onPathChange([e.target.value])}
        >
          <option value="">Select data source...</option>
          {dataPaths.map(path => (
            <option key={path} value={path}>{path}</option>
          ))}
        </Select>
      )}
    </ConfigSection>
  );
};

/**
 * Zone Configuration Editor
 */
const ZoneConfigEditor = ({ zones, onZonesChange }) => {
  const addZone = () => {
    onZonesChange([...zones, { min: 0, max: 100, color: '#10b981', label: 'Normal' }]);
  };

  const updateZone = (index, field, value) => {
    const newZones = [...zones];
    newZones[index] = { ...newZones[index], [field]: value };
    onZonesChange(newZones);
  };

  const removeZone = (index) => {
    onZonesChange(zones.filter((_, i) => i !== index));
  };

  return (
    <ConfigSection>
      <SectionTitle>Color Zones</SectionTitle>
      <ZoneEditor>
        {zones.map((zone, index) => (
          <ZoneRow key={index}>
            <Input
              type="number"
              placeholder="Min"
              value={zone.min}
              onChange={(e) => updateZone(index, 'min', parseFloat(e.target.value))}
            />
            <Input
              type="number"
              placeholder="Max"
              value={zone.max}
              onChange={(e) => updateZone(index, 'max', parseFloat(e.target.value))}
            />
            <Input
              type="color"
              value={zone.color}
              onChange={(e) => updateZone(index, 'color', e.target.value)}
            />
            <Button variant="danger" onClick={() => removeZone(index)}>×</Button>
          </ZoneRow>
        ))}
        <Button onClick={addZone}>Add Zone</Button>
      </ZoneEditor>
    </ConfigSection>
  );
};

/**
 * Dynamic Configuration Options
 */
const DynamicConfigOptions = ({ widgetType, config, onConfigChange }) => {
  const widgetTypeConfig = WIDGET_TYPES[widgetType];
  if (!widgetTypeConfig?.configOptions) return null;

  return (
    <ConfigSection>
      <SectionTitle>Display Options</SectionTitle>
      {Object.entries(widgetTypeConfig.configOptions).map(([key, option]) => (
        <ConfigRow key={key}>
          <Label>{option.label}</Label>
          {option.type === 'select' && (
            <Select
              value={config[key] || ''}
              onChange={(e) => onConfigChange({ ...config, [key]: e.target.value })}
            >
              {option.options.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
          )}
          {option.type === 'number' && (
            <Input
              type="number"
              min={option.min}
              max={option.max}
              value={config[key] || 0}
              onChange={(e) => onConfigChange({ ...config, [key]: parseFloat(e.target.value) })}
            />
          )}
          {option.type === 'boolean' && (
            <div>
              <Checkbox
                checked={config[key] || false}
                onChange={(e) => onConfigChange({ ...config, [key]: e.target.checked })}
              />
              <span>{option.label}</span>
            </div>
          )}
          {option.type === 'text' && (
            <Input
              type="text"
              value={config[key] || ''}
              onChange={(e) => onConfigChange({ ...config, [key]: e.target.value })}
            />
          )}
        </ConfigRow>
      ))}
    </ConfigSection>
  );
};

/**
 * Main Widget Configurator Component
 */
export const WidgetConfigurator = ({
  initialConfig = {},
  availableDataPaths = [],
  onConfigChange = () => {},
  onSave = () => {},
  onCancel = () => {},
  showPreview = true
}) => {
  const [widgetType, setWidgetType] = useState(initialConfig.type || 'digital');
  const [config, setConfig] = useState(initialConfig.config || {});
  const [dataPaths, setDataPaths] = useState(initialConfig.dataPaths || []);
  const [title, setTitle] = useState(initialConfig.title || 'New Widget');
  const [unit, setUnit] = useState(initialConfig.unit || '');
  const [zones, setZones] = useState(initialConfig.zones || []);

  // Update parent when configuration changes
  useEffect(() => {
    onConfigChange({
      type: widgetType,
      title,
      unit,
      config: {
        ...config,
        zones: zones.length > 0 ? zones : undefined
      },
      dataPaths
    });
  }, [widgetType, config, dataPaths, title, unit, zones]);

  // Sample data for preview
  const sampleData = {
    value: 75,
    status: 'connected',
    trend: 2.5
  };

  const widgetTypeConfig = WIDGET_TYPES[widgetType];
  const requiresMultipleDataPaths = config.displayFormat === 'multi' || widgetType === 'chart';

  return (
    <div style={{ display: 'grid', gridTemplateColumns: showPreview ? '1fr 1fr' : '1fr', gap: '2rem' }}>
      {/* Configuration Panel */}
      <ConfigPanel>
        <WidgetTypeSelector
          selectedType={widgetType}
          onTypeChange={setWidgetType}
        />

        <ConfigSection>
          <SectionTitle>Basic Settings</SectionTitle>
          <ConfigRow>
            <Label>Widget Title</Label>
            <Input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter widget title..."
            />
          </ConfigRow>
          <ConfigRow>
            <Label>Unit</Label>
            <Input
              type="text"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              placeholder="e.g., V, kts, °C"
            />
          </ConfigRow>
        </ConfigSection>

        <DataSourceSelector
          dataPaths={availableDataPaths}
          selectedPaths={dataPaths}
          onPathChange={setDataPaths}
          multiSelect={requiresMultipleDataPaths}
        />

        <DynamicConfigOptions
          widgetType={widgetType}
          config={config}
          onConfigChange={setConfig}
        />

        {(widgetType === 'gauge' || widgetType === 'bar') && (
          <ZoneConfigEditor
            zones={zones}
            onZonesChange={setZones}
          />
        )}

        <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
          <Button onClick={onSave} style={{ flex: 1 }}>
            Save Widget
          </Button>
          <Button onClick={onCancel} variant="secondary" style={{ flex: 1 }}>
            Cancel
          </Button>
        </div>
      </ConfigPanel>

      {/* Preview Panel */}
      {showPreview && (
        <div>
          <SectionTitle style={{ marginBottom: '1rem' }}>Preview</SectionTitle>
          <PreviewContainer>
            <UniversalWidget
              type={widgetType}
              config={{
                ...config,
                title,
                unit,
                zones: zones.length > 0 ? zones : undefined
              }}
              data={sampleData}
              size="medium"
            />
          </PreviewContainer>
          
          <div style={{ marginTop: '1rem', fontSize: theme.fontSizes.xs, color: theme.colors.text.secondary }}>
            <strong>Configuration Summary:</strong>
            <pre style={{ 
              background: theme.colors.background.default, 
              padding: '0.5rem', 
              borderRadius: theme.borderRadius.sm,
              marginTop: '0.5rem',
              overflow: 'auto'
            }}>
              {JSON.stringify({
                type: widgetType,
                title,
                unit,
                dataPaths,
                config: {
                  ...config,
                  zones: zones.length > 0 ? zones : undefined
                }
              }, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default WidgetConfigurator;